import { useState, useCallback, useMemo, useEffect } from "react";
import { IMaterialCallout, MaterialCallout } from "../../../entities-generated/common/material-callout";
import { MaterialStatus } from "../../../entities-generated/common/material-status";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ImmutableFormValueService, ValidatedFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged, isNotNullOrWhitespace } from "../../../framework/utils";
import { none, where } from "../../../framework/utils/collection";
import { ProjectEvents } from "../events";
import { IAssessmentRecorderMaterialFormModel, IAssessmentRecorderMaterialValueModel, IAssessmentRecorderMaterialModelChange } from "./assess-record-mat-iface";


// ##BEGIN:DEVELOPERCODE##

// this is a good place for doing things like defining custom form value service types

// ##END:DEVELOPERCODE##

// %%BEGIN:isStandardMaterial/immutable/boolean%%
export const isStandardMaterial = new (class isStandardMaterialService extends ImmutableFormValueService<IMaterialCallout, boolean> {
    public initialize(data: IMaterialCallout): void {
        this.standardMaterialId = data.standardMaterialId;
        this.value = isNotNullOrWhitespace(data.standardMaterialId);
    }

    public applyToDataModel(data: IMaterialCallout): void {
        data.standardMaterialId = this.standardMaterialId;
    }

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

    public standardMaterialId: string | undefined = undefined;

})(
    'isStandardMaterial' // id
    ,'' // description
);
// %%END:isStandardMaterial/immutable/boolean%%

// %%BEGIN:id/immutable/string%%
export const id = new (class idService extends ImmutableFormValueService<IMaterialCallout, string> {
    public initialize(data: IMaterialCallout): void {
        this.value = data.id;
    }

    public applyToDataModel(data: IMaterialCallout): void {
        data.id = this.value;
    }

    public getDefaultValue(): string {
        return '';
    }

    public getInputValue(): string {
        return this.value;
    }

})(
    'id' // id
    ,'' // description
);
// %%END:id/immutable/string%%

// %%BEGIN:description/validated/string/string%%
export const description = new (class descriptionService extends ValidatedFormValueService<IMaterialCallout, string, string> {
    public initialize(data: IMaterialCallout): void {
        this.setInputValue(new BwjInput<string>(data.description));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IMaterialCallout): void {
        data.description = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter material description');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'description' // id
    ,'Description' // description
);
// %%END:description/validated/string/string%%

// %%BEGIN:vendorUrl/validated/string/string%%
export const vendorUrl = new (class vendorUrlService extends ValidatedFormValueService<IMaterialCallout, string, string> {
    public initialize(data: IMaterialCallout): void {
        this.setInputValue(new BwjInput<string>(data.vendorUrl));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IMaterialCallout): void {
        data.vendorUrl = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter the product URL');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'vendorUrl' // id
    ,'Vendor product page URL' // description
);
// %%END:vendorUrl/validated/string/string%%

// %%BEGIN:pricePerUnit/validated/string/number%%
export const pricePerUnit = new (class pricePerUnitService extends ValidatedFormValueService<IMaterialCallout, string, number> {
    public initialize(data: IMaterialCallout): void {
        const value = data.pricePerUnit > 0 ? (data.pricePerUnit / 100).toFixed(2) : '';
        this.setInputValue(new BwjInput<string>(value));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IMaterialCallout): void {
        data.pricePerUnit = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number {
        const value = parseFloat(this.value.value);
        return isNaN(value) === false ? Math.floor(value * 100) : 0;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter product price');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'pricePerUnit' // id
    ,'Price (per unit)' // description
);
// %%END:pricePerUnit/validated/string/number%%

// %%BEGIN:quantity/validated/string/number%%
export const quantity = new (class quantityService extends ValidatedFormValueService<IMaterialCallout, string, number> {
    public initialize(data: IMaterialCallout): void {
        const value = data.quantity > 0 ? data.quantity.toString() : '';
        this.setInputValue(new BwjInput<string>(value));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IMaterialCallout): void {
        data.quantity = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number {
        const value = parseInt(this.value.value);
        return isNaN(value) === false ? value : 0;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter desired quantity');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'quantity' // id
    ,'Quantity' // description
);
// %%END:quantity/validated/string/number%%

// %%BEGIN:procurementStatus/immutable/MaterialStatus%%
export const procurementStatus = new (class procurementStatusService extends ImmutableFormValueService<IMaterialCallout, MaterialStatus> {
    public initialize(data: IMaterialCallout): void {
        this.value = data.procurementStatus;
    }

    public applyToDataModel(data: IMaterialCallout): void {
        data.procurementStatus = this.value;
    }

    public getDefaultValue(): MaterialStatus {
        return MaterialStatus.Unacquired;
    }

    public getInputValue(): MaterialStatus {
        return this.value;
    }

})(
    'procurementStatus' // id
    ,'' // description
);
// %%END:procurementStatus/immutable/MaterialStatus%%

// %%BEGIN:expectedDeliveryDate/immutable/number%%
export const expectedDeliveryDate = new (class expectedDeliveryDateService extends ImmutableFormValueService<IMaterialCallout, number> {
    public initialize(data: IMaterialCallout): void {
        this.value = data.expectedDeliveryDate;
    }

    public applyToDataModel(data: IMaterialCallout): void {
        data.expectedDeliveryDate = this.value;
    }

    public getDefaultValue(): number {
        return 0;
    }

    public getInputValue(): number {
        return this.value;
    }

})(
    'expectedDeliveryDate' // id
    ,'' // description
);
// %%END:expectedDeliveryDate/immutable/number%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): IAssessmentRecorderMaterialFormModel => {
    return {
       isStandardMaterial: isStandardMaterial.getInputValue(),
       description: description.getInputValue(),
       vendorUrl: vendorUrl.getInputValue(),
       pricePerUnit: pricePerUnit.getInputValue(),
       quantity: quantity.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): IAssessmentRecorderMaterialValueModel => {
    return {
       id: id.getOutputValue(),
       description: description.getOutputValue(),
       vendorUrl: vendorUrl.getOutputValue(),
       pricePerUnit: pricePerUnit.getOutputValue(),
       quantity: quantity.getOutputValue(),
       procurementStatus: procurementStatus.getOutputValue(),
       expectedDeliveryDate: expectedDeliveryDate.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        description.getInputValue(),
        vendorUrl.getInputValue(),
        pricePerUnit.getInputValue(),
        quantity.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: IAssessmentRecorderMaterialModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(ProjectEvents.AssessRecordMatModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<IAssessmentRecorderMaterialFormModel>> => {
    const promises = [
        description.formValidateAndUpdate(),
        vendorUrl.formValidateAndUpdate(),
        pricePerUnit.formValidateAndUpdate(),
        quantity.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:IAssessmentRecorderMaterialFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: IAssessmentRecorderMaterialValueModel, dataModel: IMaterialCallout } = {
    dataModel: new MaterialCallout()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: IMaterialCallout) => {
    const dataModel = data || new MaterialCallout();

    isStandardMaterial.initialize(dataModel);
    id.initialize(dataModel);
    description.initialize(dataModel);
    vendorUrl.initialize(dataModel);
    pricePerUnit.initialize(dataModel);
    quantity.initialize(dataModel);
    procurementStatus.initialize(dataModel);
    expectedDeliveryDate.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:IAssessmentRecorderMaterialFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
description.setChangePublisher(broadcastChange);
vendorUrl.setChangePublisher(broadcastChange);
pricePerUnit.setChangePublisher(broadcastChange);
quantity.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new MaterialCallout();

    isStandardMaterial.applyToDataModel(dataModel);
    id.applyToDataModel(dataModel);
    description.applyToDataModel(dataModel);
    vendorUrl.applyToDataModel(dataModel);
    pricePerUnit.applyToDataModel(dataModel);
    quantity.applyToDataModel(dataModel);
    procurementStatus.applyToDataModel(dataModel);
    expectedDeliveryDate.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: IAssessmentRecorderMaterialModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], ProjectEvents.AssessRecordMatModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): IAssessmentRecorderMaterialFormModel => {
    const [getter, setter] = useState<IAssessmentRecorderMaterialFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: IAssessmentRecorderMaterialModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: IAssessmentRecorderMaterialFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('AssessmentRecorderMaterialFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('AssessmentRecorderMaterialFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toMaterialCallout = () => {
    const v = toValueModel();
    const callout: IMaterialCallout = {
        standardMaterialId: isStandardMaterial.standardMaterialId,
        id: v.id,
        description: v.description,
        vendorUrl: v.vendorUrl,
        pricePerUnit: v.pricePerUnit,
        quantity: v.quantity,
        procurementStatus: v.procurementStatus,
        expectedDeliveryDate: v.expectedDeliveryDate
    };
    return callout;
};

// ##END:DEVELOPERCODE##
